/**
 * scratchFunctions.ts
 * -------------------
 * Contains the functions used in the path "/scratch"
 * of the API. It allows the handling of the request and
 * the sending of the response.
 * This file is consumed by "./src/routes/scratch.ts"
 */
import JSZip from "jszip";
import { toScratchblocks } from "utils/scratch-parser/index.js";

// HAT_BLOCKS to filter Scratch hat blocks
const HAT_BLOCKS = [
  "event_whenflagclicked",
  "event_whenkeypressed",
  "event_whengreaterthan",
  "event_whenthisspriteclicked",
  "event_whenstageclicked",
  "event_whenbackdropswitchesto",
  "event_whenbroadcastreceived",
  "control_start_as_clone",
  "procedures_definition",
  "boost_whenColor",
  "boost_whenTilted",
  "ev3_whenButtonPressed",
  "ev3_whenDistanceLessThan",
  "ev3_whenBrightnessLessThan",
  "gdxfor_whenGesture",
  "gdxfor_whenForcePushedOrPulled",
  "gdxfor_whenTilted",
  "makeymakey_whenMakeyKeyPressed",
  "makeymakey_whenCodePressed",
  "microbit_whenButtonPressed",
  "microbit_whenGesture",
  "microbit_whenTilted",
  "microbit_whenPinConnected",
  "wedo2_whenDistance",
  "wedo2_whenTilted",
];

const PREVENT_BLOCKS_HEAD = [
  "motion_xposition",
  "motion_yposition",
  "motion_direction",
  "looks_costumenumbername",
  "looks_backdropnumbername",
  "looks_size",
  "sound_volume",
  "sensing_touchingobject",
  "sensing_touchingcolor",
  "sensing_coloristouchingcolor",
  "sensing_distanceto",
  "sensing_answer",
  "sensing_keypressed",
  "sensing_mousedown",
  "sensing_mousex",
  "sensing_mousey",
  "sensing_loudness",
  "sensing_timer",
  "sensing_of",
  "sensing_current",
  "sensing_dayssince2000",
  "sensing_username",
  "operator_add",
  "operator_subtract",
  "operator_multiply",
  "operator_divide",
  "operator_random",
  "operator_gt",
  "operator_lt",
  "operator_equals",
  "operator_and",
  "operator_or",
  "operator_not",
  "operator_join",
  "operator_letter_of",
  "operator_length",
  "operator_contains",
  "operator_mod",
  "operator_round",
  "operator_mathop",
  "data_itemoflist",
  "data_itemnumoflist",
  "data_lengthoflist",
  "data_listcontainsitem",
  "procedures_call",
  "argument_reporter_string_number",
  "argument_reporter_boolean",
];

/**
 * Function used to transform a SB3 file in JSON format to text
 * @param jsonSb3File
 * @returns
 */
export const jsonSB3ToText = async (jsonSb3File: any) => {
  // Get the sprites of this json file
  const spriteOptions = jsonSb3File.targets
    .filter((target: any) => !target.isStage)
    .map((target: any) => target.name);

  // Define the outputs
  let outText = "";
  const outObj: { [key: string]: string } = {};

  // For each one of the sprites
  spriteOptions.forEach((targetName: string) => {
    // Get the scratch blocks for this sprite
    const thisBlocks = generateScratchblocks(
      jsonSb3File.targets.filter((item: any) => item.name === targetName)[0],
    );

    // Format the text output
    if (outText.length > 0) outText += "\n\n";
    outText += `# ${targetName}\n`;
    outText += thisBlocks;

    // And format the JSON output
    outObj[targetName] = thisBlocks;
  });

  // Define the output
  return { text: outText, obj: outObj };
};

/**
 * Function used to transform a raw sb3 file into a text or json file.
 * @param sb3File SB3 file in base64 format (string).
 * @param to (optional) Output format. By default is `"text"`.
 * @returns
 */
export const convertSB3File = async (
  sb3File: Buffer | string | null,
  to: "string" | "json" = "string",
) => {
  // Check if there's any content (return empty string or undefined)
  if (!sb3File) return sb3File;

  try {
    // Define the sb3Buffer
    let sb3Buffer: Buffer | undefined;

    // Base64 String case
    if (typeof sb3File === "string") {
      // Extract the base64 part from the Data URL
      const base64Match = sb3File.match(/^data:[^;]+;base64,(.+)$/);
      if (!base64Match) {
        throw new Error("Invalid Data URL format");
      }

      // Get the data
      const base64Data = base64Match[1];

      // Decode base64 to binary
      sb3Buffer = Buffer.from(base64Data, "base64");
    }
    // Buffer case
    else if (typeof sb3File === "object") {
      sb3Buffer = sb3File;
    } else {
      throw new Error("Invalid SB3 file format");
    }

    // Check the buffer is defined appropriately
    if (!sb3Buffer) throw new Error("No SB3 file found");

    // Check if the buffer starts with the ZIP file signature
    if (sb3Buffer.readUInt32BE(0) !== 0x504b0304) {
      throw new Error("Invalid ZIP file signature");
    }

    // Use JSZip to read the project.json file
    const zip = await JSZip.loadAsync(sb3Buffer as any);

    // Get the project file
    const projectJsonFile = zip.file("project.json");
    if (!projectJsonFile) {
      throw new Error("project.json not found in the sb3 file");
    }

    // Get the content
    const projectJsonContent = await projectJsonFile.async("string");
    const projectJson = JSON.parse(projectJsonContent);

    // Transform this JSON file to text
    const { text, obj } = await jsonSB3ToText(projectJson);

    // Define the output
    return to === "string" ? text : obj;
  } catch (error) {
    console.error("Error processing sb3 file:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Stack trace:", error.stack);
    }
    return null;
  }
};

/**
 * Auxiliar function used to get the code of a particular
 * target inside a Scratch file
 * @param target
 * @returns
 */
const generateScratchblocks = (target: any) => {
  // Before trying to get the code, make sure they're patched
  const patchedBlocks = patchMissingBlockRefs(target.blocks);

  const hatBlocksKeys = Object.keys(patchedBlocks).filter((key) => {
    const blockItem = patchedBlocks[key];
    return (
      blockItem.topLevel && !PREVENT_BLOCKS_HEAD.includes(blockItem.opcode)
    );
  });

  const scratchblocksCode = hatBlocksKeys
    .map((hatKey) => {
      return toScratchblocks(hatKey, patchedBlocks, "en", {
        tab: " ".repeat(4),
        variableStyle: "as-needed",
      });
    })
    .join("\n\n");

  return scratchblocksCode;
};

/**
 * Auxiliar function to sanitise a Scratch block dictionary
 * by removing or replacing references to non-existent blocks.
 * The Scratch VM's `se()` function traverses `next` pointers and
 * `SUBSTACK` inputs in a do-while loop and throws if a referenced
 * block ID is absent from the dictionary. This function nullifies
 * dangling `next` pointers, deletes `SUBSTACK` inputs whose targets
 * are missing, and replaces other missing block references with a
 * harmless literal string so that the VM can execute without crashing.
 * @param blocks Object containing the blocks for a particular target
 */
const patchMissingBlockRefs = (
  blocks: Record<string, any>,
): Record<string, any> => {
  // Define the output as a copy of the current blocks
  const patched: Record<string, any> = JSON.parse(JSON.stringify(blocks));

  for (const block of Object.values(patched)) {
    // Patch `next` pointers — se() follows these in a do-while loop and crashes
    // if the target block doesn't exist in the dictionary.
    if (typeof block.next === "string" && !patched[block.next]) {
      block.next = null;
    }

    // Check if block has input field
    if (!block.inputs || typeof block.inputs !== "object") continue;

    // And check the inputs
    for (const [inputKey, inputValue] of Object.entries(block.inputs) as any) {
      // Check just the cases of inputs that are in this format:
      // - "inputs": { "SUBSTACK": [1, null] },
      // - "inputs": { "DEGREES": [1, [4, "90"]] },
      // - "inputs": { "VALUE": [1, [4, "1"]] },
      if (!Array.isArray(inputValue)) continue;

      // Get the value (and later check if it's a real referenceId)
      const referencedId = inputValue[1];

      // Skip literal value arrays like [4, "15"] — not block references
      if (Array.isArray(referencedId)) continue;

      // referencedId is null (empty body) or a string ID pointing to a missing block
      if (!referencedId || !patched[referencedId]) {
        if (inputKey.startsWith("SUBSTACK")) {
          // se() is called with this value — null or missing ID both crash it.
          // To solve it, it's necessary to void it (or delete it).
          // In this case, I just deleted it
          block.inputs = {};
        } else if (typeof referencedId === "string") {
          // Only replace string IDs pointing to missing blocks, leave null as-is
          block.inputs[inputKey] = [1, [10, "?"]];
        }
      }
    }
  }

  return patched;
};
